/**
 * Starile de plata — cele 6 culori din foaia `legenda`, plus `unset` explicit si stari terminale.
 *
 * De ce exista `unset`: din 833 de celule de plata populate, 712 nu au nicio culoare
 * semnificativa (594 alb, 100 fara fill, 18 alb-tematic). A deduce "aprobat" din lipsa culorii
 * ar fi o inventie periculoasa. Culoarea a fost mereu o adnotare pe setul de lucru curent, nu o
 * arhiva: histograma pe coloanele Q..AM arata 20 galben, 14 cyan, 1 magenta pe tot orizontul.
 *
 * Culoarea nu este NICIODATA singurul canal de codificare. Fiecare stare are patru:
 * glif (silueta distincta), eticheta in romana, pastila colorata si bara de 3px. Sase nuante de
 * status nu pot fi facute separabile doar prin nuanta pentru cititorii cu deficiente de vedere a
 * culorilor (rosu-verde si galben-portocaliu sunt coliziunile clasice), deci glifui e obligatoriu,
 * nu decorativ.
 */

export const STATUS_CODES = [
  'unset',
  'approved',
  'blocked',
  'staged',
  'waiting',
  'verify',
  'urgent',
  'paid',
  'postponed',
  'cancelled',
] as const;

export type StatusCode = (typeof STATUS_CODES)[number];

export interface StatusMeta {
  code: StatusCode;
  /** Eticheta afisata, exact formularea din foaia `legenda` unde exista. */
  labelRo: string;
  /** Descrierea completa din legenda, pentru tooltip. */
  descriptionRo: string | null;
  /** Litera pentru comanda rapida de la tastatura si pentru celula compacta. */
  shortCode: string;
  /** Alias numeric stabil (1..6), pentru cine prefera cifrele. */
  numericAlias: number | null;
  /** Silueta glifului; componenta React o mapeaza la un SVG inline. */
  glyph:
    | 'none'
    | 'check-circle'
    | 'slash-octagon'
    | 'stacked-bars'
    | 'clock'
    | 'question-diamond'
    | 'exclaim-triangle'
    | 'banknote'
    | 'arrow-right-circle'
    | 'x';
  /** ARGB-ul original din Excel, pentru round-trip in export. */
  argb: string | null;
  isApproved: boolean;
  /** `blocati` si `plata esalonata` sunt decizii deliberate, nu inactiune. */
  blocksPayment: boolean;
  isTerminal: boolean;
  /** Intra in totalul `De plata` al rundei? Starile terminale nu intra. */
  countsInTotal: boolean;
  /**
   * Eligibila pentru amanarea automata de la 18:00.
   * `blocked` si `staged` NU sunt: ar sterge judecata unui om.
   */
  autopostponeEligible: boolean;
  sortOrder: number;
}

export const STATUS: Readonly<Record<StatusCode, StatusMeta>> = {
  unset: {
    code: 'unset',
    labelRo: 'Nemarcat',
    descriptionRo: 'Nicio stare setata — plata e planificata, dar nedecisa.',
    shortCode: '',
    numericAlias: null,
    glyph: 'none',
    argb: null,
    isApproved: false,
    blocksPayment: false,
    isTerminal: false,
    countsInTotal: true,
    autopostponeEligible: true,
    sortOrder: 0,
  },
  approved: {
    code: 'approved',
    labelRo: 'Aprobat',
    descriptionRo: 'plati aprobate/zi-Mihai Patrascu',
    shortCode: 'A',
    numericAlias: 1,
    glyph: 'check-circle',
    argb: 'FF00FF00',
    isApproved: true,
    blocksPayment: false,
    isTerminal: false,
    countsInTotal: true,
    autopostponeEligible: false,
    sortOrder: 1,
  },
  blocked: {
    code: 'blocked',
    labelRo: 'Blocat',
    descriptionRo: 'blocati',
    shortCode: 'B',
    numericAlias: 2,
    glyph: 'slash-octagon',
    argb: 'FFFF0000',
    isApproved: false,
    blocksPayment: true,
    isTerminal: false,
    countsInTotal: true,
    autopostponeEligible: false,
    sortOrder: 2,
  },
  staged: {
    code: 'staged',
    labelRo: 'Esalonat',
    descriptionRo: 'plata esalonata',
    shortCode: 'E',
    numericAlias: 3,
    glyph: 'stacked-bars',
    argb: 'FFFFFF00',
    isApproved: false,
    blocksPayment: false,
    isTerminal: false,
    countsInTotal: true,
    autopostponeEligible: false,
    sortOrder: 3,
  },
  waiting: {
    code: 'waiting',
    labelRo: 'In asteptare',
    descriptionRo: 'in asteptare',
    shortCode: 'T',
    numericAlias: 4,
    glyph: 'clock',
    argb: 'FF00FFFF',
    isApproved: false,
    blocksPayment: false,
    isTerminal: false,
    countsInTotal: true,
    autopostponeEligible: true,
    sortOrder: 4,
  },
  verify: {
    code: 'verify',
    labelRo: 'De verificat',
    descriptionRo:
      'de verificat daca s-a facut receptia la externe / de verificat soldul la interni',
    shortCode: 'V',
    numericAlias: 5,
    glyph: 'question-diamond',
    argb: 'FFFF00FF',
    isApproved: false,
    blocksPayment: false,
    isTerminal: false,
    countsInTotal: true,
    autopostponeEligible: false,
    sortOrder: 5,
  },
  urgent: {
    code: 'urgent',
    labelRo: 'Urgent',
    descriptionRo: 'plata urgenta ca sa nu ne blocheze',
    shortCode: 'U',
    numericAlias: 6,
    glyph: 'exclaim-triangle',
    argb: 'FFFF9900',
    isApproved: false,
    blocksPayment: false,
    isTerminal: false,
    countsInTotal: true,
    autopostponeEligible: true,
    sortOrder: 6,
  },
  paid: {
    code: 'paid',
    labelRo: 'Platit',
    descriptionRo: 'Plata a fost executata.',
    shortCode: 'P',
    numericAlias: 7,
    glyph: 'banknote',
    argb: null,
    isApproved: true,
    blocksPayment: false,
    isTerminal: true,
    countsInTotal: true,
    autopostponeEligible: false,
    sortOrder: 7,
  },
  postponed: {
    code: 'postponed',
    labelRo: 'Amanat',
    descriptionRo: 'Plata a fost mutata pe alta zi; suma se numara pe ziua-tinta.',
    shortCode: '',
    numericAlias: null,
    glyph: 'arrow-right-circle',
    argb: null,
    isApproved: false,
    blocksPayment: false,
    isTerminal: true,
    // NU intra in total: altfel aceeasi suma ar fi numarata si in ziua veche si in cea noua
    countsInTotal: false,
    autopostponeEligible: false,
    sortOrder: 8,
  },
  cancelled: {
    code: 'cancelled',
    labelRo: 'Anulat',
    descriptionRo: 'Plata nu se mai face.',
    shortCode: '',
    numericAlias: null,
    glyph: 'x',
    argb: null,
    isApproved: false,
    blocksPayment: false,
    isTerminal: true,
    countsInTotal: false,
    autopostponeEligible: false,
    sortOrder: 9,
  },
};

/**
 * Starile din legenda, in ordinea in care le ciclez cu `Space`.
 *
 * `paid` a fost adaugata la cererea colegelor de la contabilitate („Plata efectuata > Platit").
 * Ea era deja setabila cu tasta `P` — `statusFromShortCode` se construieste din tot catalogul —
 * dar lipsea din legenda, deci nimeni nu stia ca exista. Iar banda de legenda isi asuma explicit
 * ca ordinea ei E ordinea ciclului, deci o stare care apare in banda TREBUIE sa fie si aici.
 *
 * `paid` ramane `isTerminal: true`: nu se cicleaza mai departe intr-o amanare, iar
 * `postpone.ts` refuza amanarea unei stari terminale. Terminal inseamna „nu se mai muta", nu
 * „nu se mai poate corecta" — o bifa pusa gresit trebuie sa se poata da inapoi.
 */
export const CYCLE_ORDER: readonly StatusCode[] = [
  'unset',
  'approved',
  'blocked',
  'staged',
  'waiting',
  'verify',
  'urgent',
  'paid',
];

/** Ciclarea cu `Space` (inainte) si `Shift+Space` (inapoi). */
export function cycleStatus(current: StatusCode, direction: 1 | -1 = 1): StatusCode {
  const idx = CYCLE_ORDER.indexOf(current);
  // O stare terminala nu se cicleaza — se iese din ea prin actiuni explicite.
  if (idx === -1) return current;
  const next = (idx + direction + CYCLE_ORDER.length) % CYCLE_ORDER.length;
  return CYCLE_ORDER[next] as StatusCode;
}

const BY_SHORT_CODE = new Map<string, StatusCode>();
const BY_NUMERIC = new Map<number, StatusCode>();
const BY_ARGB = new Map<string, StatusCode>();
for (const meta of Object.values(STATUS)) {
  if (meta.shortCode) BY_SHORT_CODE.set(meta.shortCode.toUpperCase(), meta.code);
  if (meta.numericAlias !== null) BY_NUMERIC.set(meta.numericAlias, meta.code);
  if (meta.argb) BY_ARGB.set(meta.argb.toUpperCase(), meta.code);
}

/** `'a'` sau `'A'` => `'approved'`. Pentru comenzile de la tastatura. */
export function statusFromShortCode(key: string): StatusCode | null {
  return BY_SHORT_CODE.get(key.toUpperCase()) ?? null;
}

/** `1..6` => codul corespunzator. */
export function statusFromNumericAlias(n: number): StatusCode | null {
  return BY_NUMERIC.get(n) ?? null;
}

/**
 * Culoarea de fill din Excel => stare. Doar cele 6 din legenda se mapeaza.
 *
 * Alte fill-uri intalnite in fisier (`FF78EB30` pe TP/LC validate, `FF6FA8DC` pe coloanele de
 * control, `FFF4CCCC`, `FFFFFEEF`, `FFAAFF00`, `FFEAD1DC`) NU sunt stari: se pastreaza in
 * `raw_fill_argb` si se semnaleaza pentru revizuire. A le ghici ar inventa informatie.
 */
export function statusFromFillArgb(argb: string | null | undefined): StatusCode | null {
  if (!argb) return null;
  const normalized = argb.toUpperCase();
  // Excel scrie uneori fara canalul alfa (6 caractere)
  const key = normalized.length === 6 ? `FF${normalized}` : normalized;
  return BY_ARGB.get(key) ?? null;
}

/** Culorile care apar in fisier dar NU sunt stari — se pastreaza, nu se interpreteaza. */
export const NON_STATUS_FILLS: Readonly<Record<string, string>> = {
  FFFFFFFF: 'fundal alb implicit al foii',
  FF78EB30: 'TP / LC validate',
  FF6FA8DC: 'coloanele de control CUI si denumire',
  FFF4CCCC: 'adnotare de rand, semnificatie neconfirmata',
  FFFFFEEF: 'adnotare de rand, semnificatie neconfirmata',
  FFAAFF00: 'nemapat in legenda — de confirmat cu echipa',
  FFEAD1DC: 'nemapat in legenda — de confirmat cu echipa',
  FF00FFFF: 'in asteptare (si folosit pe furnizori in foaia valutara)',
  FFEFEFEF: 'randuri derivate din Cashflow',
  FFF3F3F3: 'randuri de sumar din Cashflow',
  FF666666: 'eticheta Target accesare linie',
};

/** Starea efectiva: suprascrierea din aplicatie bate valoarea importata. */
export function effectiveStatus(
  imported: StatusCode | null | undefined,
  override: StatusCode | null | undefined,
): StatusCode {
  return override ?? imported ?? 'unset';
}

export function isStatusCode(value: unknown): value is StatusCode {
  return typeof value === 'string' && (STATUS_CODES as readonly string[]).includes(value);
}

/** Starile care intra in totalul `De plata` al unei runde. */
export const STATUSES_IN_TOTAL: readonly StatusCode[] = STATUS_CODES.filter(
  (c) => STATUS[c].countsInTotal,
);

/** Starile pe care le atinge matura de amanare automata de la 18:00. */
export const STATUSES_AUTOPOSTPONE: readonly StatusCode[] = STATUS_CODES.filter(
  (c) => STATUS[c].autopostponeEligible,
);
